'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback } from '@extern/ui/avatar';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@extern/ui/input-otp';
import { InputPassword, InputPasswordTooltip } from '@extern/ui/input-password';
import { Text } from '@extern/ui/text';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailboxIcon, PasswordIcon } from '@phosphor-icons/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { passwordWithConfirmationSchema } from '@/infra/schemas/auth';
import { useSignIn } from '../_hooks/use-sign-in';
const CodeSchema = z.object({
  code: z.string().min(6, {
    message: 'Please enter a valid code'
  })
});
const cardClassName = 'flex flex-col w-full sm:w-[358px] max-w-full items-center justify-center gap-6 overflow-clip rounded-3xl border border-role-outline-subtle bg-white p-6';
export function SignInResetPasswordExperiment() {
  const $ = _c(62);
  const {
    onSubmitNewPassword,
    onVerifyResetCode,
    onResendResetCode,
    fetchStatus,
    signIn,
    errors
  } = useSignIn();
  const [resendTimeLeft, setResendTimeLeft] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(CodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const codeForm = useForm(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(passwordWithConfirmationSchema),
      defaultValues: {
        password: "",
        confirmPassword: ""
      },
      mode: "onChange"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const passwordForm = useForm(t1);
  let t2;
  let t3;
  if ($[2] !== resendTimeLeft) {
    t2 = () => {
      let timer;
      if (resendTimeLeft > 0) {
        timer = setTimeout(() => setResendTimeLeft(_temp), 1000);
      }
      return () => clearTimeout(timer);
    };
    t3 = [resendTimeLeft];
    $[2] = resendTimeLeft;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (signIn.status === "needs_new_password") {
    let t4;
    if ($[5] !== onSubmitNewPassword || $[6] !== passwordForm) {
      let t5;
      if ($[8] !== onSubmitNewPassword) {
        t5 = data => onSubmitNewPassword(data.password);
        $[8] = onSubmitNewPassword;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      t4 = passwordForm.handleSubmit(t5);
      $[5] = onSubmitNewPassword;
      $[6] = passwordForm;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex w-full flex-col items-center justify-center gap-6 text-center"><Avatar size="48"><AvatarFallback className="bg-role-info-container text-role-on-info-container" size="48" variant="icon"><PasswordIcon size={20} /></AvatarFallback></Avatar><Text className="text-role-on-background" title="lg">Please set your new password</Text></div>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    let t7;
    if ($[11] !== passwordForm.control) {
      t6 = <FormField control={passwordForm.control} name="password" render={_temp2} />;
      t7 = <FormField control={passwordForm.control} name="confirmPassword" render={_temp3} />;
      $[11] = passwordForm.control;
      $[12] = t6;
      $[13] = t7;
    } else {
      t6 = $[12];
      t7 = $[13];
    }
    const t8 = passwordForm.formState.isSubmitting || !passwordForm.formState.isValid;
    const t9 = fetchStatus === "fetching";
    let t10;
    if ($[14] !== t8 || $[15] !== t9) {
      t10 = <Button appearance="filled" aria-label="Update password" className="w-full" color="info" data-cy="update-password" disabled={t8} isLoading={t9} type="submit">Update password</Button>;
      $[14] = t8;
      $[15] = t9;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    let t11;
    if ($[17] !== t10 || $[18] !== t6 || $[19] !== t7) {
      t11 = <div className="flex w-full flex-col gap-4">{t6}{t7}{t10}</div>;
      $[17] = t10;
      $[18] = t6;
      $[19] = t7;
      $[20] = t11;
    } else {
      t11 = $[20];
    }
    let t12;
    if ($[21] !== t11 || $[22] !== t4) {
      t12 = <form className={cardClassName} onSubmit={t4}>{t5}{t11}</form>;
      $[21] = t11;
      $[22] = t4;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    let t13;
    if ($[24] !== passwordForm || $[25] !== t12) {
      t13 = <Form {...passwordForm}>{t12}</Form>;
      $[24] = passwordForm;
      $[25] = t12;
      $[26] = t13;
    } else {
      t13 = $[26];
    }
    return t13;
  }
  let t4;
  if ($[27] !== codeForm || $[28] !== onVerifyResetCode) {
    let t5;
    if ($[30] !== onVerifyResetCode) {
      t5 = data_0 => onVerifyResetCode(data_0.code);
      $[30] = onVerifyResetCode;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    t4 = codeForm.handleSubmit(t5);
    $[27] = codeForm;
    $[28] = onVerifyResetCode;
    $[29] = t4;
  } else {
    t4 = $[29];
  }
  let t5;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Avatar size="48"><AvatarFallback className="bg-role-info-container text-role-on-info-container" size="48" variant="icon"><MailboxIcon size={20} /></AvatarFallback></Avatar>;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  let t6;
  if ($[33] !== signIn.identifier) {
    t6 = <div className="flex w-full flex-col items-center justify-center gap-6 text-center">{t5}<Text body="lg" className="text-role-on-background">To continue, enter the code sent to <span className="font-medium">{signIn.identifier}</span></Text></div>;
    $[33] = signIn.identifier;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== errors.fields.code) {
    t7 = t8 => {
      const {
        field: field_1
      } = t8;
      return <FormItem><FormControl><InputOTP maxLength={6} {...field_1}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl>{errors.fields.code ? <FormMessage>{errors.fields.code.message}</FormMessage> : <FormMessage />}</FormItem>;
    };
    $[35] = errors.fields.code;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  let t8;
  if ($[37] !== codeForm.control || $[38] !== t7) {
    t8 = <div className="flex w-full flex-col gap-2"><FormField control={codeForm.control} name="code" render={t7} /></div>;
    $[37] = codeForm.control;
    $[38] = t7;
    $[39] = t8;
  } else {
    t8 = $[39];
  }
  const t9 = codeForm.formState.isSubmitting || !codeForm.formState.isValid;
  const t10 = fetchStatus === "fetching";
  let t11;
  if ($[40] !== t10 || $[41] !== t9) {
    t11 = <div className="flex w-full gap-4"><Button appearance="filled" aria-label="Continue" className="flex-1" color="info" data-cy="verify-reset-code" disabled={t9} isLoading={t10} type="submit">Continue</Button></div>;
    $[40] = t10;
    $[41] = t9;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const t12 = resendTimeLeft > 0;
  let t13;
  if ($[43] !== onResendResetCode) {
    t13 = async () => {
      setResendTimeLeft(60);
      await onResendResetCode();
    };
    $[43] = onResendResetCode;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const t14 = resendTimeLeft > 0 ? `${resendTimeLeft}s` : "60s";
  let t15;
  if ($[45] !== t14) {
    t15 = <span className="text-role-info">Resend ({t14})</span>;
    $[45] = t14;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t12 || $[48] !== t13 || $[49] !== t15) {
    t16 = <Button appearance="ghost" className="gap-0" disabled={t12} onClick={t13} type="button">Didn't receive a code? {t15}</Button>;
    $[47] = t12;
    $[48] = t13;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t11 || $[52] !== t16 || $[53] !== t6 || $[54] !== t8) {
    t17 = <div className={cardClassName}>{t6}{t8}{t11}{t16}</div>;
    $[51] = t11;
    $[52] = t16;
    $[53] = t6;
    $[54] = t8;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t17 || $[57] !== t4) {
    t18 = <form className="w-full" onSubmit={t4}>{t17}</form>;
    $[56] = t17;
    $[57] = t4;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  let t19;
  if ($[59] !== codeForm || $[60] !== t18) {
    t19 = <Form {...codeForm}>{t18}</Form>;
    $[59] = codeForm;
    $[60] = t18;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  return t19;
}
function _temp3(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormLabel>Confirm password</FormLabel><FormControl><InputPassword autoCapitalize="none" autoComplete="new-password" autoCorrect="off" data-cy="confirm-password" id="confirm-password" key="confirmPassword" placeholder="********" {...field_0} /></FormControl><FormMessage /></FormItem>;
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel className="flex items-center gap-2">New password<InputPasswordTooltip value={field.value} /></FormLabel><FormControl><InputPassword autoCapitalize="none" autoComplete="new-password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="********" {...field} /></FormControl><FormMessage /></FormItem>;
}
function _temp(prev) {
  return prev - 1;
}